/**
 * # Accordion
 *
 * @example
 * new Spark.Accordion(el);
 *
 * @module components/accordion.js
 * @since 2.13.0
 */

import BaseComponent from './base';

import addClass from '../helpers/dom/add-class';
import hasClass from '../helpers/dom/has-class';
import removeClass from '../helpers/dom/remove-class';
import getParent from '../helpers/traversal/get-parent';
import matches from '../helpers/traversal/matches';

const onOpenEvent = document.createEvent('Event');
const onCloseEvent = document.createEvent('Event');

onOpenEvent.initEvent('open', true, true);
onCloseEvent.initEvent('close', true, true);

/**
 * Private Helper Method to Expand an Accordion Drawer
 * @param {String} drawer
 * @return {Promise}
 * @private
 */
const expand = function(drawer) {
  const self = this;
  return new Promise(resolve => {
    const control = this.el.querySelector('.spark-accordion__header[aria-controls="' + drawer + '"]');
    const content = this.el.querySelector('#' + drawer);
    const height = Math.round(content.scrollHeight);

    control.setAttribute('aria-expanded', true);
    content.style.visibility = 'visible';
    content.style.height = height + 'px';

    content.addEventListener('transitionend', function onExpand() {
      content.removeEventListener('transitionend', onExpand);
      content.style.height = self.expanded.includes(drawer) ? 'auto' : 0 + 'px';
      onOpenEvent.drawer = drawer;
      self.el.dispatchEvent(onOpenEvent);
      resolve();
    });
  });
};


/**
 * Private Helper Method to Collapse an Accordion Drawer
 * @param {String} drawer
 * @return {Promise}
 * @private
 */
const collapse = function(drawer) {
  const self = this;
  return new Promise(resolve => {
    const control = this.el.querySelector('.spark-accordion__header[aria-controls="' + drawer + '"]');
    const content = this.el.querySelector('#' + drawer);
    const height = content.scrollHeight;
    const transition = content.style.transition;

    content.style.visibility = 'visible';
    content.style.transition = '';

    content.addEventListener('transitionend', function onCollapse() {
      content.removeEventListener('transitionend', onCollapse);
      control.setAttribute('aria-expanded', false);
      content.style.visibility = 'hidden';
    });

    requestAnimationFrame(() => {
      // Define height explicitly, because transitions on "auto" won't visually animate
      content.style.height = height + 'px';

      // trigger another transition
      content.style.transition = transition;

      requestAnimationFrame(() => {
        // set height to 0, so that the transition may animate from the fixed height to 0
        content.style.height = 0 + 'px';
      });

      control.setAttribute('aria-expanded', false);
      onCloseEvent.drawer = drawer;
      self.el.dispatchEvent(onCloseEvent);
      resolve();
    });
  });
};

/**
 * Accordion
 * @class creates an Accordion
 * @extends {BaseComponent}
 */
class Accordion extends BaseComponent {

  /**
   * Accordion constructor.
   * @param {Element} el
   * @param {Object} params
   * @constructor
   */
  constructor(el, params = {}) {

    super(el, params);

    this._bindEventListenerCallbacks();
    this._addEventListeners();

    if (this.radio) {
      this.multiExpand = false;
      this.collapsible = false;

      if (!hasClass(this.el, 'spark-accordion--radio')) {
        addClass(this.el, 'spark-accordion--radio');
      }
    }
  }


  /**
   * Sets up references
   * @param  {Element} el base DOM element
   * @private
   */
  _cacheElements(el) {
    this.el = el;
    if (hasClass(this.el, 'spark-accordion--radio')) {
      this.radio = true;
    }
  }


  /**
   * Create bound versions of event listener callbacks and store them.
   * Otherwise we can't unbind from these events later because the
   * function signatures won't match.
   * @private
   */
  _bindEventListenerCallbacks() {
    this._onClickBound = this._onClick.bind(this);
    this._onKeyDownBound = this._onKeyDown.bind(this);
    this._onChangeBound = this._onChange.bind(this);
    this._onOpenBound = this._onOpen.bind(this);
    this._onCloseBound = this._onClose.bind(this);
    this._onFocusBound = this._onFocus.bind(this);
    this._onBlurBound = this._onBlur.bind(this);
  }


  /**
   * Add event listeners for DOM events.
   * @private
   */
  _addEventListeners() {
    if (this.el) {
      this.el.addEventListener('click', this._onClickBound, true);
      this.el.addEventListener('keydown', this._onKeyDownBound, true);
      this.el.addEventListener('change', this._onChangeBound, true);
      this.el.addEventListener('open', this._onOpenBound, true);
      this.el.addEventListener('close', this._onCloseBound, true);

      // Added as MS Hack, for enabling the addition and removal of a `focus` class,
      // to circumvent a lack of support in Internet Explorer & Edge for :focus-within
      // TODO: Remove focus/blur based event code & associated .focus CSS styles,
      // upon Edge browser support for :focus-within, and dropping of Internet Explorer
      this.el.addEventListener('focus', this._onFocusBound, true);
      this.el.addEventListener('blur', this._onBlurBound, true);
    }
  }


  /**
   * Remove event listeners for DOM events..
   * @private
   */
  _removeEventListeners() {
    if (this.el) {
      this.el.removeEventListener('click', this._onClickBound, true);
      this.el.removeEventListener('keydown', this._onKeyDownBound, true);
      this.el.removeEventListener('change', this._onChangeBound, true);
      this.el.removeEventListener('open', this._onOpenBound, true);
      this.el.removeEventListener('close', this._onCloseBound, true);
      this.el.removeEventListener('focus', this._onFocusBound, true);
      this.el.removeEventListener('blur', this._onBlurBound, true);
    }
  }


  /**
   * On Key Down (ENTER or ARROW UP/DOWN keys), trigger accordion controls
   * @param {Event} e
   * @private
   */
  _onKeyDown(e) {
    const ENTER_KEY_CODE = 13;
    const SPACE_KEY_CODE = 32;
    const ARROW_UP_KEY_CODE = 38;
    const ARROW_DOWN_KEY_CODE = 40;

    if (this.radio && e.keyCode !== ENTER_KEY_CODE) return;

    const control = hasClass(e.target, 'spark-accordion__header') ? e.target : getParent(e.target, '.spark-accordion__header', this.el);
    const drawer = control && this._isActionable(control.getAttribute('aria-controls')) ? control.getAttribute('aria-controls') : undefined;

    if (drawer) {
      switch (e.keyCode) {
        case ENTER_KEY_CODE:
        case SPACE_KEY_CODE:
          if (this.radio) {
            this.select(drawer);
          } else {
            this.toggle(drawer);
          }
          break;
        case ARROW_DOWN_KEY_CODE:
          if (this.collapsed.includes(drawer)) {
            this.open(drawer);
          } else {
            e.preventDefault();
          }
          break;
        case ARROW_UP_KEY_CODE:
          if (this.expanded.includes(drawer)) {
            this.close(drawer);
          } else {
            e.preventDefault();
          }
          break;
      }
    }
  }


  /**
   * On Click, if target is a radio control, emulate `ENTER` key for Windows Browsers
   * @params {Object} e
   * @private
   */
  _onClick(e) {
    const headerClick = hasClass(e.target, 'spark-accordion__header');
    const header = headerClick ? e.target : getParent(e.target, '.spark-accordion__header', this.el);

    if (header) {
      const drawer = header.getAttribute('aria-controls');
      const isActionable = this._isActionable(drawer);

      if (isActionable && this.radio) {
        this.select(drawer);
      } else if (isActionable) {
        this.toggle(drawer);
      }
    }
  }


  /**
   * On Focus, add a class for Internet Explorer styles
   * @param {Object} e
   * @private
   */
  _onFocus(e) {
    if (matches(e.target, 'header')) {
      addClass(e.target, 'focus');
    } else if (matches(e.target, '.spark-radio__input')) {
      const header = getParent(e.target, '.spark-accordion__header', this.el);
      addClass(header, 'focus');
    }
  }


  /**
   * On Blur, remove class added for Internet Explorer styles
   * @param {Object} e
   * @private
   */
  _onBlur(e) {
    if (matches(e.target, 'header')) {
      removeClass(e.target, 'focus');
    } else if (matches(e.target, '.spark-radio__input')) {
      const header = getParent(e.target, '.spark-accordion__header', this.el);
      removeClass(header, 'focus');
    }
  }


  /**
   * On Radio change, make sure drawers match selection
   * @param {Event} e
   * @private
   */
  _onChange(e) {
    if (matches(e.target, '.spark-accordion__header > .spark-radio > .spark-radio__input')) {
      const header = getParent(e.target, '.spark-accordion__header', this.el);
      const drawer = header && this.drawers.includes(header.getAttribute('aria-controls')) ? header.getAttribute('aria-controls') : undefined;

      if (drawer) this.select(drawer);
    }
  }


  /**
   * Fire `onOpen` callback
   * @param {Event} e
   * @private
   */
  _onOpen(e) {
    this.onOpen.call(this, e);
  }


  /**
   * Fire `onClose` callback
   * @param {Event} e
   * @private
   */
  _onClose(e) {
    this.onClose.call(this, e);
  }


  /**
   * Returns whether or not a drawer exists, and it’s current expanded/collapsed state can be changed
   * @param {String} drawer
   * @return {Boolean}
   * @private
   */
  _isActionable(drawer) {
    const isExpanded = this.expanded.includes(drawer);
    const isCollapsed = this.collapsed.includes(drawer);

    if (!this.drawers.includes(drawer)) {
      return false;
    } else if (this.radio) {
      const radio = this.el.querySelector('.spark-accordion__header[aria-controls="' + drawer + '"] .spark-radio .spark-radio__input');
      return radio && !radio.checked && !radio.hasAttribute('disabled') ? true : false;
    } else if (!this.collapsible) {
      if (isExpanded && this.expanded.length === 1) {
        return false;
      } else if (isExpanded && this.expanded.length > 1) {
        return true;
      } else if (isCollapsed) {
        return true;
      }
    } else if (!this.multiExpand) {
      if (isExpanded && this.collapsible) {
        return true;
      } else if (!this.collapsible && isExpanded) {
        return false;
      } else if (isCollapsed) {
        return true;
      }
    } else {
      return true;
    }
  }


  /**
   * Return collection of all drawers
   * @return {Array}
   * @public
   */
  get drawers() {
    const drawerControls = this.el.querySelectorAll('.spark-accordion__header');
    const drawerContents = this.el.querySelectorAll('.spark-accordion__content');
    const drawerNames = drawerControls.length ? Array.from(drawerControls).map(control => control.getAttribute('aria-controls')) : [];
    return drawerNames.filter(drawer => Array.from(drawerContents).some(content => content.id === drawer));
  }


  /**
   * Return collection of currently expanded drawers
   * @return {Array}
   * @public
   */
  get expanded() {
    const openDrawerControls = this.el.querySelectorAll('.spark-accordion__header[aria-expanded="true"]');
    return openDrawerControls.length ? Array.from(openDrawerControls).map(control => control.getAttribute('aria-controls')) : [];
  }


  /**
   * Explicitly set which drawers are currently expanded
   * @param {Array} drawers
   * @public
   */
  set expanded(drawers) {
    if (!this.radio) {
      const toClose = this.expanded.filter(expanded => !drawers.includes(expanded));
      toClose.forEach(drawer => this.close(drawer).catch(() => {}));
      drawers.forEach(drawer => this.open(drawer).catch(() => {}));
    } else if (drawers.length >= 1) {
      this.select(drawers.pop());
    }
  }


  /**
   * Return collection of currently collapsed drawers
   * @return {Array}
   * @public
   */
  get collapsed() {
    const openDrawerControls = this.el.querySelectorAll('.spark-accordion__header[aria-expanded="false"]');
    return openDrawerControls.length ? Array.from(openDrawerControls).map(control => control.getAttribute('aria-controls')) : [];
  }


  /**
   * Explicitly set which drawers are currently expanded
   * @param {Array} drawers
   * @public
   */
  set collapsed(drawers) {
    if (!this.radio) {
      const toOpen = this.collapsed.filter(collapsed => !drawers.includes(collapsed));
      toOpen.forEach(drawer => this.open(drawer).catch(() => {}));
      drawers.forEach(drawer => this.close(drawer).catch(() => {}));
    } else {
      throw new Error('Cannot be used to control radio accordions');
    }
  }


  /**
   * Returns the current radio value
   * @return {String}
   * @public
   */
  get selected() {
    if (!this.radio) {
      return undefined;
    } else {
      const checkedRadio = this.el.querySelector('.spark-accordion__header .spark-radio > .spark-radio__input:checked');
      return checkedRadio ? checkedRadio.value : undefined;
    }
  }


  /**
   * Activate an accordion drawer by radio value
   * @param {String} value
   * @public
   */
  set selected(value) {
    if (!this.radio) {
      return;
    } else if (!value) {
      this.clearValue();
    } else {
      const radio = this.el.querySelector('.spark-accordion__header .spark-radio > .spark-radio__input[value="' + value + '"]');
      const drawer = getParent(radio, '.spark-accordion__header', this.el).getAttribute('aria-controls');
      if (this.drawers.includes(drawer)) this.select(drawer);
    }
  }


  /**
   * Expand accordion drawer
   * @param {String} drawer
   * @return {Promise}
   * @public
   */
  open(drawer) {
    const self = this;
    return new Promise((resolve, reject) => {
      const drawers = self.drawers;
      const expanded = self.expanded;
      const collapsed = self.collapsed;
      const isActionable = self._isActionable(drawer);

      if (!drawers.includes(drawer)) {
        reject(new Error('Drawer either does not exist, or configured incorrectly'));
      } else if (expanded.includes(drawer)) {
        resolve();
      } else if (collapsed.includes(drawer) && !isActionable) {
        reject(new Error('Drawer could not be opened'));
      } else if (collapsed.includes(drawer) && isActionable && self.radio) {
        self.select(drawer).then(resolve);
      } else if (collapsed.includes(drawer) && isActionable && self.multiExpand) {
        expand.call(self, drawer).then(resolve);
      } else if (collapsed.includes(drawer) && isActionable && !self.multiExpand) {
        if (expanded.length > 0) {
          collapse.call(self, expanded[0]).then(() => {
            expand.call(self, drawer).then(resolve);
          });
        } else {
          expand.call(self, drawer).then(resolve);
        }
      } else {
        resolve();
      }
    });
  }


  /**
   * Collapse accordion drawer
   * @param {String} drawer
   * @return {Promise}
   * @public
   */
  close(drawer) {
    const self = this;
    return new Promise((resolve, reject) => {
      const drawers = self.drawers;
      const expanded = self.expanded;
      const collapsed = self.collapsed;
      const isActionable = self._isActionable(drawer);

      if (!drawers.includes(drawer)) {
        reject(new Error('Drawer either does not exist, or configured incorrectly'));
      } else if (collapsed.includes(drawer)) {
        resolve();
      } else if (expanded.includes(drawer) && !isActionable) {
        reject(new Error('Drawer could not be closed'));
      } else if (isActionable) {
        collapse.call(self, drawer).then(resolve);
      } else {
        resolve();
      }
    });
  }


  /**
   * Tell the accordion to toggle drawer if drawer is valid
   * @param {String} drawer
   * @public
   */
  toggle(drawer) {
    const self = this;

    return new Promise((resolve, reject) => {
      const isActionable = self._isActionable(drawer);

      if (!self.drawers.includes(drawer)) {
        reject(new Error('Drawer either does not exist, or configured incorrectly'));
      } else if (self.radio && isActionable) {
        self.clearValue().then(() => {
          return self.select(drawer);
        }).then(resolve).catch(() => {
          reject(new Error('Radio Drawer could not be opened'));
        });
      } else if (self.expanded.includes(drawer) && isActionable) {
        self.close(drawer).then(resolve);
      } else if (self.expanded.includes(drawer) && !isActionable) {
        reject(new Error('Drawer could not be closed'));
      } else if (self.collapsed.includes(drawer) && isActionable) {
        if (self.multiExpand) {
          self.open(drawer).then(resolve);
        } else {
          self.hide().then(() => {
            self.open(drawer).then(resolve);
          });
        }
      } else if (self.collapsed.includes(drawer) && !isActionable) {
        reject(new Error('Drawer could not be opened'));
      }
    });
  }


  /**
   * Check radio
   * @param {String} drawer
   * @return {Promise}
   * @public
   */
  select(drawer) {
    const self = this;
    return new Promise((resolve, reject) => {
      if (self.radio && drawer === self.expanded[0]) {
        resolve();
      } else if (self.radio && self.drawers.includes(drawer)) {
        const radio = self.el.querySelector('.spark-accordion__header[aria-controls="' + drawer + '"] .spark-radio__input:enabled');
        if (radio) {
          self.clearValue().then(() => {
            radio.checked = true;
            expand.call(self, drawer).then(resolve);
          });
        } else {
          reject(new Error('Cannot select radio button "' + drawer + '"'));
        }
      } else {
        reject(new Error('Not a radio accordion'));
      }
    });
  }


  /**
   * Uncheck all radios and collapse open drawers - only works for Radio Accordions
   * @return {Promise}
   * @public
   */
  clearValue() {
    const self = this;
    return new Promise(resolve => {
      if (self.radio && self.expanded.length) {
        const drawer = self.expanded[0];
        const radio = self.el.querySelector('.spark-accordion__header[aria-controls="' + drawer + '"] .spark-radio__input');
        radio.checked = false;
        collapse.call(self, drawer).then(resolve);
      } else if (!self.radio) {
        console.info('clearValue() only effects radio accordions, but was run anyway');
      } else {
        resolve();
      }
    });
  }


  /**
   * Expand all drawers (Chevron - `multiExpand`) or enable radio buttons (Radio)
   * @return {Promise}
   * @public
   */
  show() {
    const self = this;
    return new Promise((resolve, reject) => {
      if (self.radio) {
        const radioButtons = self.el.querySelectorAll('.spark-accordion__header .spark-radio__input');
        if (radioButtons.length >= 1) {
          radioButtons.forEach(radio => radio.removeAttribute('disabled'));
          resolve();
        } else {
          reject(new Error('Accordion is either misconfigured, or there are no drawers to enable'));
        }
      } else if (self.multiExpand) {
        const collapsed = self.collapsed;
        switch (collapsed.length) {
          case 0:
            resolve();
            break;
          case 1:
            expand.call(self, collapsed[0]).then(resolve);
            break;
          default: {
            const changes = [];
            do {
              changes.push(expand.call(self, collapsed.shift()));
            } while (collapsed.length);

            Promise.all(changes).then(
              resolve
            ).catch(reject);
            break;
          }
        }
      } else {
        const expanded = self.expanded;
        if (expanded.length && expanded[0] !== self.drawers[0]) {
          collapse.call(self, expanded[0]);
          expand.call(self, self.drawers[0]).then(resolve);
        } else if (expanded.length) {
          resolve();
        } else {
          reject(new Error('Accordion is either misconfigured, or there are no drawers to open'));
        }
      }
    });
  }


  /**
   * Collapse all drawers (Chevron) or disable radio buttons (radio)
   * @return {Promise}
   * @public
   */
  hide() {
    const self = this;
    return new Promise((resolve, reject) => {
      if (self.radio) {
        const radioButtons = self.el.querySelectorAll('.spark-accordion__header .spark-radio__input');
        if (radioButtons.length >= 1) {
          radioButtons.forEach(button => {
            const disabled = document.createAttribute('disabled');
            button.attributes.setNamedItem(disabled);
          });
          resolve();
        } else {
          reject(new Error('Accordion is either misconfigured, or there are no drawers to disable'));
        }
      } else {
        const expanded = self.expanded;
        switch (expanded.length) {
          case 0:
            resolve();
            break;
          case 1:
            collapse.call(self, expanded[0]).then(resolve);
            break;
          default: {
            const changes = [];
            do {
              changes.push(collapse.call(self, expanded.shift()));
            } while (expanded.length);

            Promise.all(changes).then(
              resolve
            ).catch(reject);
            break;
          }
        }
      }
    });
  }


  /**
   * Remove component from DOM.
   * @param {Boolean} leaveElement
   * @public
   */
  remove(leaveElement) {
    return super.remove(leaveElement);
  }


  /**
   * Update the element in use and the position.
   * @param {Element} el
   * @public
   */
  update(el, params) {
    return super.update(el, params);
  }
}

/**
 * Whitelisted parameters which can be set on construction.
 * @type {Array}
 */
Accordion.prototype._whitelistedParams = [
  'radio',
  'multiExpand',
  'collapsible',
  'onOpen',
  'onClose'
];

/**
 * Default values for internal properties we will be setting.
 * These are set on each construction so we don't leak properties
 * into the prototype chain.
 * @type {Object}
 */
Accordion.prototype.defaults = {
  el: null,
  radio: false,
  multiExpand: true,
  collapsible: true,
  onOpen: () => {},
  onClose: () => {}
};

export default Accordion;
